import React from 'react';
import _ from 'underscore';

import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as CreateContainerModal from 'samples/containers/CreateContainerModal';
import AddIcon from 'ui-library/icons/AddIcon';
import uniqueId from 'util/uniqueId';

import * as StorableActions from './StorableActions';
import StorableInfoTableFromListing from './StorableInfoTableFromListing';
import {ContainableContainerListing, ContainablePlateListing} from './StorableStore';

const CONTAINER_LIMIT = 50;
const PLATE_LIMIT = 50;
const LISTING_OPTIONS = {includeStorableSummaryExtras: true};

export interface ContainableStorageTablesProps {
  containableId: string;
  containerListings: {
    [key: string]: ContainableContainerListing;
  };
  plateListings: {
    [key: string]: ContainablePlateListing;
  };
  _containerListingId?: string;
  _plateListingId?: string;
}

interface ContainableStorageTablesState {
  containerListingId: string;
  plateListingId: string;
  selectedContainerArchivePurposes: Array<string>;
  selectedPlateArchivePurposes: Array<string>;
}

export default class ContainableStorageTables extends React.Component<
  ContainableStorageTablesProps,
  ContainableStorageTablesState
> {
  override state: ContainableStorageTablesState = {
    containerListingId: this.props._containerListingId || uniqueId('listingId'),
    plateListingId: this.props._plateListingId || uniqueId('listingId'),
    selectedContainerArchivePurposes: [NOT_ARCHIVED_PURPOSE_STRING],
    selectedPlateArchivePurposes: [NOT_ARCHIVED_PURPOSE_STRING],
  };

  override componentDidMount(): void {
    _.defer(() => {
      this.handleContainerSetOffset(0);
      this.handlePlatesSetOffset(0);
    });
  }

  override componentWillUnmount(): void {
    const {containerListingId, plateListingId} = this.state;
    _.defer(() => {
      StorableActions.clearContainerListing(containerListingId);
      StorableActions.clearPlateListing(plateListingId);
    });
  }

  override UNSAFE_componentWillReceiveProps(nextProps: ContainableStorageTablesProps): void {
    if (
      this.props.containerListings[this.state.containerListingId] &&
      !this.props.containerListings[this.state.containerListingId].isStale &&
      nextProps.containerListings[this.state.containerListingId].isStale
    ) {
      _.defer(() => {
        this.handleContainerSetOffset(0);
      });
    }
    if (
      this.props.plateListings[this.state.plateListingId] &&
      !this.props.plateListings[this.state.plateListingId].isStale &&
      nextProps.plateListings[this.state.plateListingId].isStale
    ) {
      _.defer(() => {
        this.handlePlatesSetOffset(0);
      });
    }
  }

  private handleContainerSetOffset = (offset: number): void => {
    StorableActions.listContainableContainers(
      this.state.containerListingId,
      this.props.containableId,
      offset,
      CONTAINER_LIMIT,
      {...LISTING_OPTIONS, archivePurposes: this.state.selectedContainerArchivePurposes}
    );
  };

  private handleOpenCreateContainers = (): void => {
    CreateContainerModal.open({
      initialRegistry: null,
      initialInputContainableId: this.props.containableId,
      disallowContainableEdit: true,
      shouldIncludeStorableSummaryExtrasOnRelist: true,
      initialParentStorableId: null,
      actionOrigin: ActionOrigin.REGISTRY_ITEM_PAGE_STORAGE_TAB,
    });
  };

  private handlePlatesSetOffset = (offset: number): void => {
    StorableActions.listContainablePlates(
      this.state.plateListingId,
      this.props.containableId,
      offset,
      PLATE_LIMIT,
      {...LISTING_OPTIONS, archivePurposes: this.state.selectedPlateArchivePurposes}
    );
  };

  private handleContainerChangeArchivePurposes = (selectedContainerArchivePurposes: Array<string>): void => {
    this.setState({selectedContainerArchivePurposes});
    StorableActions.listContainableContainers(
      this.state.containerListingId,
      this.props.containableId,
      0,
      CONTAINER_LIMIT,
      {...LISTING_OPTIONS, archivePurposes: selectedContainerArchivePurposes}
    );
  };

  private handlePlateChangeArchivePurposes = (selectedPlateArchivePurposes: Array<string>): void => {
    this.setState({selectedPlateArchivePurposes});
    StorableActions.listContainablePlates(
      this.state.plateListingId,
      this.props.containableId,
      0,
      CONTAINER_LIMIT,
      {...LISTING_OPTIONS, archivePurposes: selectedPlateArchivePurposes}
    );
  };

  override render(): JSX.Element {
    return (
      <div>
        {(() => (
          <div className="containableSamples-containers">
            <div className="u-spacing-bottom">
              <h3>
                Containers
                <AddIcon onClick={this.handleOpenCreateContainers} />
              </h3>
              <StorableInfoTableFromListing
                emptyTableText="No containers"
                listing={this.props.containerListings[this.state.containerListingId]}
                containableId={this.props.containableId}
                onChangeArchivePurposes={this.handleContainerChangeArchivePurposes}
                selectedArchivePurposes={this.state.selectedContainerArchivePurposes}
                setOffset={this.handleContainerSetOffset}
                itemWord="container"
                displayPrintLabelButton={true}
              />
            </div>
            <div>
              <h3>Plates</h3>
              <StorableInfoTableFromListing
                emptyTableText="No plates"
                listing={this.props.plateListings[this.state.plateListingId]}
                onChangeArchivePurposes={this.handlePlateChangeArchivePurposes}
                selectedArchivePurposes={this.state.selectedPlateArchivePurposes}
                setOffset={this.handlePlatesSetOffset}
                itemWord="plate"
                displayPrintLabelButton={true}
              />
            </div>
          </div>
        ))()}
      </div>
    );
  }
}
